/// <reference path="Modal.ts" />


/**
 * Modal options with extra form properties.
 */
interface MacrailModalFormOptions extends MacrailModalOptions {
    /**
     * URL to load the form HTML from.
     */
    loadUrl: string;
    /**
     * Extra parameters to pass to the loadUrl page.
     */
    loadData: any;
    /**
     * URL to submit the form to.
     */
    submitUrl: string;
    /**
     * Extra parameters to pass to the sumbitUrl page.
     */
    submitData: any;
    /**
     * Callback for when saving succeeds.
     */
    onSaveSuccess?: (data: any, status: string) => void;
    /**
     * Callback for when saving fails.
     */
    onSaveFailure?: (data: any, status: string) => void;

    /**
     * What to do automatically for a successful save.
     * * none - do nothing.
     * * closeonly - replace the save and cancel buttons with just close.
     */
    saveSuccessAction: 'none' | 'closeonly';
}


namespace MACRAIL {

/**
 * A specialised Modal dialog with built-in support for showing and submitting a form.
 *
 * The life cycle is:
 * - Add properties to `loadData` to set what the form needs to display, such as the ID of a selected record.
 * - Call `show()` and the modal will POST `loadData` to the `loadUrl` via AJAX, and replace the body with the response.
 * - Clicking the 'Save' button will collect any form fields in the body, and POST that to `submitUrl`.
 * - Depending on the JSON reply, either a error alert will appear, or the body will be cleared and the dialog will be
 *   ready to close.
 */
export class ModalForm extends Modal {

    protected opts: MacrailModalFormOptions;

    /**
     * Create a Modal dialog with built in handling of form submissions.
     *
     * @constructor
     * @param opts Initialisation options. See also [[MacrailModalOptions]].
     */
    constructor (opts: MacrailModalFormOptions) {

        opts = $.extend({
            'loadUrl': '',
            'loadData': {},
            'submitData': {},
            'buttons': [{
                'name': 'save',
                'text': 'Save',
                'class': 'btn-success',
            }, {
                'name': 'cancel',
                'text': 'Cancel',
                'role': 'dismiss',
            }],
            'saveSuccessAction': 'closeonly',
        }, opts || {});

        super(opts);
    }

    /**
     * Show the form and load the body.
     *
     * @return void
     */
    public show () {
        this.setButtons(this.opts.buttons);
        super.show();
        this.showLoader();
        this.clearAlert();
        if (this.opts.loadUrl === '') {
            throw 'Modal::show - loadUrl is blank';
        }
        this.loadBody(this.opts.loadUrl, this.opts.loadData);
    }

    /**
     * Called after the initial load of the HTML content for the dialog.
     */
    public _formOnBodyLoad () {
        let self = this;

        let form = this.bodyDiv.find('form');

        // Prevent the default action of submitting the form to a url with a GET request.
        form.on('submit', function (e) {
            e.preventDefault();
        });

        // Only allow submitted when pressing enter in a normal text field. This does not fit exactly with
        // browser standards, but is at least less broken then it was before.
        form.on('keyup', 'input[type="text"]:not(.ui-autocomplete-input)', function (e) {
            if (e.keyCode === 13) {
                e.preventDefault();
                self._onButtonClicked('save');
            }
        });
    }

    /**
     * Called when a button is pressed.
     */
    protected _onButtonClicked (button: string) {
        switch (button) {
            case 'save':
                this.setAlert('info', 'Saving...');
                this.setButtonEnabled('save', false);
                let form = this.bodyDiv.find('form');
                let formData = form.serializeArray();

                if (!jQuery.isEmptyObject(this.opts.submitData)) {
                    $.each(this.opts.submitData, function(k, v) {
                        formData.push({
                            'name': k,
                            'value': v,
                        });
                    });
                }

                $(':input', form).each(function() {
                    // Remove previous error highlighting
                    $(this).parents('.form-group').removeClass('has-error');
                });

                $.post(this.opts.submitUrl, formData).always($.proxy(this._onSaveResponse, this));
                break;
            case 'close':
                this.hide();
                break;
        }
    }

    /**
     * Called when the AJAX reply to the save in received.
     */
    protected _onSaveResponse (data: any, status: string) {

        this.setButtonEnabled('save', true);

        if (status !== 'success' || typeof data !== 'object') {
            this.setAlert('danger', 'An unexpected error occured while saving. Please refresh the page and try again.');
            return;
        }

        if (data.status !== 'success') {
            this.setAlert(data.alertClass || 'danger', data.message);

            if (data.fields !== undefined) {
                let form = this.bodyDiv.find('form');

                $.each(data.fields, function(k, v) {
                    $('[name=' + v + ']', form).parents('.form-group').addClass('has-error');
                });
            }

            if (typeof this.opts.onSaveFailure === 'function') {
                this.opts.onSaveFailure(data, status);
            }
            return;
        }

        this.setAlert(data.alertClass || 'success', data.message);

        switch (this.opts.saveSuccessAction) {
            case 'closeonly':
                this.setButtons(Modal.defaultButtonGroups.close);
                break;
        }

        if (typeof this.opts.onSaveSuccess === 'function') {
            this.opts.onSaveSuccess(data, status);
        }
    }
}

}
